import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, useDataProvider, useLocaleState, useNotify, useTranslate } from "react-admin";

import {
  GetIntegrationImportPreviewQuery,
  IntegrationExecutionStatus,
  IntegrationPreviewChange,
  LocationTypeEnum,
  SensoRawDataEndpoint,
} from "../generated/graphql";
import { formatDateTimeInline } from "../lib/dateUtils";
import { space } from "../theme/designTokens";
import { SensoRawDataDialog, sensoRawDataEndpoint } from "./SensoRawDataDialog";

const POLL_INTERVAL_MS = 2000;

type PreviewData = NonNullable<GetIntegrationImportPreviewQuery["integrationImportPreview"]>;
type PreviewLocation = PreviewData["locations"][number];
type PreviewPerson = PreviewData["patients"][number];
type PreviewIssue = PreviewData["failures"][number];
type LocationRef = { type: LocationTypeEnum; externalId: string };

const CHANGES = [
  IntegrationPreviewChange.New,
  IntegrationPreviewChange.Changed,
  IntegrationPreviewChange.Unchanged,
  IntegrationPreviewChange.Failed,
];

const changeColors: Record<IntegrationPreviewChange, "success" | "warning" | "default" | "error"> = {
  [IntegrationPreviewChange.New]: "success",
  [IntegrationPreviewChange.Changed]: "warning",
  [IntegrationPreviewChange.Unchanged]: "default",
  [IntegrationPreviewChange.Failed]: "error",
};

/**
 * Backend reasons that have a translation. Any other reason is provider text
 * and is shown as it came.
 */
const knownReasonKeys: Record<string, string> = {
  "Missing patient name": "missingPatientName",
  "Invalid birth date": "invalidBirthDate",
  "Organization has no root location": "noRootLocation",
};

const isExpired = (preview: PreviewData) => new Date(preview.expiresAt).getTime() <= Date.now();

const knownEntities = ["location", "patient", "practitioner", "procedure"];

const locationKey = (ref: LocationRef) => `${ref.type}:${ref.externalId}`;

type TreeNode = {
  key: string;
  location: PreviewLocation;
  parent: TreeNode | null;
  children: TreeNode[];
  residents: number;
};

/**
 * Builds the location tree from the parent references. A location whose parent
 * is missing or unknown is a root. A resident counts once at the location they
 * are assigned to and once at every ancestor of it.
 */
const buildTree = (preview: PreviewData): { roots: TreeNode[]; unassigned: number } => {
  const nodes = new Map<string, TreeNode>();
  preview.locations.forEach(location => {
    const key = locationKey(location);
    if (!nodes.has(key)) {
      nodes.set(key, { key, location, parent: null, children: [], residents: 0 });
    }
  });

  const roots: TreeNode[] = [];
  nodes.forEach(node => {
    const parent = node.location.parent ? nodes.get(locationKey(node.location.parent)) : undefined;
    if (parent && parent !== node) {
      node.parent = parent;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const residentsByNode = new Map<string, Set<number>>();
  preview.patients.forEach((patient, index) => {
    patient.locations.forEach(ref => {
      const visited = new Set<string>();
      let current = nodes.get(locationKey(ref));
      while (current && !visited.has(current.key)) {
        visited.add(current.key);
        const residents = residentsByNode.get(current.key) ?? new Set<number>();
        residents.add(index);
        residentsByNode.set(current.key, residents);
        current = current.parent ?? undefined;
      }
    });
  });
  nodes.forEach(node => {
    node.residents = residentsByNode.get(node.key)?.size ?? 0;
  });

  return { roots, unassigned: preview.patients.filter(patient => patient.locations.length === 0).length };
};

const countByChange = (items: { change: IntegrationPreviewChange }[]) =>
  CHANGES.map(change => items.filter(item => item.change === change).length);

const personReference = (person: PreviewPerson) => person.number || person.externalId;

/**
 * Failed people carry their reason on the person. Add them to the failures
 * unless the backend already listed the same entity and reference.
 */
const collectFailures = (preview: PreviewData): PreviewIssue[] => {
  const seen = new Set(preview.failures.map(issue => `${issue.entity}\u0000${issue.reference ?? ""}`));
  const failures = [...preview.failures];
  const addFailedPeople = (entity: string, people: PreviewPerson[]) =>
    people.forEach(person => {
      if (person.change !== IntegrationPreviewChange.Failed || !person.reason) return;
      const reference = personReference(person);
      const id = `${entity}\u0000${reference}`;
      if (seen.has(id)) return;
      seen.add(id);
      failures.push({ entity, reference, reason: person.reason });
    });
  addFailedPeople("patient", preview.patients);
  addFailedPeople("practitioner", preview.practitioners);
  return failures;
};

const ChangeChip = ({ change }: { change: IntegrationPreviewChange }) => {
  const translate = useTranslate();
  return (
    <Chip
      size="small"
      variant="outlined"
      color={changeColors[change]}
      label={translate(`integration.preview.change.${change}`)}
    />
  );
};

const LocationTreeNode = ({ node }: { node: TreeNode }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;

  const content = (
    <>
      <ListItemText
        primary={node.location.name}
        secondary={translate(`integration.preview.locationType.${node.location.type}`)}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: space.xs }}>
        <ChangeChip change={node.location.change} />
        <Typography variant="body2" color="text.secondary">
          {translate("integration.preview.tree.residents", { smart_count: node.residents })}
        </Typography>
        {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
      </Box>
    </>
  );

  return (
    <>
      <ListItem disablePadding>
        {hasChildren ? (
          <ListItemButton onClick={() => setOpen(prev => !prev)} aria-expanded={open}>
            {content}
          </ListItemButton>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", px: space.xs, py: space.xxs }}>
            {content}
          </Box>
        )}
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: space.xs }}>
            {node.children.map(child => (
              <LocationTreeNode key={child.key} node={child} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const IssueList = ({ title, issues }: { title: string; issues: PreviewIssue[] }) => {
  const translate = useTranslate();
  if (issues.length === 0) return null;
  return (
    <Box sx={{ mt: space.sm }}>
      <Typography variant="subtitle2">{title}</Typography>
      <List dense disablePadding>
        {issues.map((issue, index) => {
          const reasonKey = knownReasonKeys[issue.reason];
          const entity = knownEntities.includes(issue.entity)
            ? translate(`integration.preview.entity.${issue.entity}`)
            : issue.entity;
          return (
            <ListItem key={`${issue.entity}:${issue.reference ?? ""}:${index}`} disableGutters>
              <ListItemText
                primary={reasonKey ? translate(`integration.preview.reason.${reasonKey}`) : issue.reason}
                secondary={
                  issue.reference
                    ? translate("integration.preview.issueReference", { entity, reference: issue.reference })
                    : entity
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

type RawDataTarget = { endpoint: SensoRawDataEndpoint; method: string; path: string };

const SensoRequestList = ({
  requests,
  canShowRawData,
  onShowRawData,
}: {
  requests: SensoRequest[];
  canShowRawData: boolean;
  onShowRawData: (target: RawDataTarget) => void;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  if (requests.length === 0) return null;
  const rawDataButton = ({ method, path }: SensoRequest) => {
    const endpoint = sensoRawDataEndpoint(method, path);
    if (!canShowRawData || endpoint === null || path === null) return undefined;
    return (
      <Button
        size="small"
        color="secondary"
        onClick={() => onShowRawData({ endpoint, method, path })}
        label="integration.preview.rawData.button"
      />
    );
  };
  const seconds = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const result = (request: SensoRequest): string => {
    const parts: string[] = [];
    if (request.status === null) {
      if (request.errorKind === "timeout") {
        parts.push(translate("integration.preview.requests.timeout"));
      } else if (request.errorKind && /^[A-Z][A-Z0-9_]*$/.test(request.errorKind)) {
        parts.push(translate("integration.preview.requests.networkError", { code: request.errorKind }));
      } else {
        parts.push(translate("integration.preview.requests.failed"));
      }
    } else {
      parts.push(
        request.oraCodes.length > 0
          ? translate("integration.preview.requests.httpWithCodes", {
              status: request.status,
              codes: request.oraCodes.join(", "),
            })
          : translate("integration.preview.requests.http", { status: request.status })
      );
      if (request.count !== null) {
        parts.push(translate("integration.preview.requests.records", { smart_count: request.count }));
      }
    }
    parts.push(
      translate("integration.preview.requests.duration", { seconds: seconds.format(request.durationMs / 1000) })
    );
    return parts.join(", ");
  };

  return (
    <Box sx={{ mt: space.sm }}>
      <Typography variant="subtitle2">{translate("integration.preview.requests.title")}</Typography>
      <List dense disablePadding sx={{ maxHeight: 240, overflow: "auto" }}>
        {requests.map((request, index) => (
          // The list never reorders, and the same request can repeat, so the position identifies a line.
          // eslint-disable-next-line react/no-array-index-key
          <ListItem key={index} disableGutters secondaryAction={rawDataButton(request)}>
            <ListItemText
              primary={`${request.method} ${request.path ?? translate("integration.preview.requests.unknownPath")}: ${result(request)}`}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const PreviewContent = ({ preview }: { preview: PreviewData }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const tree = useMemo(() => buildTree(preview), [preview]);
  const failures = useMemo(() => collectFailures(preview), [preview]);

  const totals = [
    { key: "locations", counts: countByChange(preview.locations) },
    { key: "patients", counts: countByChange(preview.patients) },
    { key: "practitioners", counts: countByChange(preview.practitioners) },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {translate("integration.preview.createdAt", { date: formatDateTimeInline(preview.createdAt, locale) })}
        {" · "}
        {translate("integration.preview.expiresAt", { date: formatDateTimeInline(preview.expiresAt, locale) })}
      </Typography>

      <Table size="small" sx={{ mt: space.sm }}>
        <TableHead>
          <TableRow>
            <TableCell>{translate("integration.preview.totals.title")}</TableCell>
            {CHANGES.map(change => (
              <TableCell key={change} align="right">
                {translate(`integration.preview.change.${change}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {totals.map(row => (
            <TableRow key={row.key}>
              <TableCell component="th" scope="row">
                {translate(`integration.preview.totals.${row.key}`)}
              </TableCell>
              {row.counts.map((count, index) => (
                <TableCell key={CHANGES[index]} align="right">
                  {count}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="body2" color="text.secondary" mt={space.xs}>
        {translate("integration.preview.procedureCount", { smart_count: preview.procedureCount })}
      </Typography>

      <Typography variant="subtitle2" mt={space.sm}>
        {translate("integration.preview.tree.title")}
      </Typography>
      {tree.roots.length > 0 ? (
        <List disablePadding sx={{ maxHeight: 400, overflow: "auto" }}>
          {tree.roots.map(root => (
            <LocationTreeNode key={root.key} node={root} />
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {translate("integration.preview.tree.empty")}
        </Typography>
      )}
      <Typography variant="body2" mt={space.xs}>
        {translate("integration.preview.tree.unassigned", { smart_count: tree.unassigned })}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {translate("integration.preview.tree.unassignedHint")}
      </Typography>

      <IssueList title={translate("integration.preview.warnings")} issues={preview.warnings} />
      <IssueList title={translate("integration.preview.failures")} issues={failures} />
    </Box>
  );
};

type PreviewFailure = { status: IntegrationExecutionStatus; reason: string | null };

/** One SENSO request of a run, as the integrations service stored it in the execution summary. */
type SensoRequest = {
  method: string;
  path: string | null;
  status: number | null;
  count: number | null;
  durationMs: number;
  errorKind: string | null;
  oraCodes: string[];
};

type FinishedRun = { status: IntegrationExecutionStatus; errorMessage?: string | null; summary?: unknown };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A number of at least the minimum, null when absent, undefined when malformed. */
const nullableNumber = (value: unknown, minimum: number): number | null | undefined => {
  if (value === null || value === undefined) return null;
  return typeof value === "number" && Number.isFinite(value) && value >= minimum ? value : undefined;
};

/** Reads one request entry; anything that does not have the stored shape is dropped. */
const parseRequest = (value: unknown): SensoRequest | null => {
  if (!isRecord(value)) return null;
  const { method, path, status, count, durationMs, errorKind, oraCodes } = value;
  if (typeof method !== "string" || !/^[A-Z]{3,7}$/.test(method)) return null;
  if (path !== null && path !== undefined && (typeof path !== "string" || !path.startsWith("/"))) return null;
  const parsedStatus = nullableNumber(status, 100);
  const parsedCount = nullableNumber(count, 0);
  if (parsedStatus === undefined || parsedCount === undefined) return null;
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs < 0) return null;
  if (errorKind !== undefined && errorKind !== null && typeof errorKind !== "string") return null;
  if (oraCodes !== undefined && !Array.isArray(oraCodes)) return null;
  return {
    method,
    path: path ?? null,
    status: parsedStatus,
    count: parsedCount,
    durationMs,
    errorKind: errorKind ?? null,
    oraCodes: (oraCodes ?? []).filter((code): code is string => typeof code === "string" && /^ORA-\d{5}$/.test(code)),
  };
};

const parseRequests = (summary: unknown): SensoRequest[] => {
  if (!isRecord(summary) || !Array.isArray(summary.requests)) return [];
  return summary.requests.map(parseRequest).filter((request): request is SensoRequest => request !== null);
};

type IntegrationImportPreviewProps = {
  organizationId: string;
  /** The preview and the import run with the saved settings, so unsaved edits block them. */
  disabled: boolean;
  onImport: () => void;
  importing: boolean;
};

export const IntegrationImportPreview = ({
  organizationId,
  disabled,
  onImport,
  importing,
}: IntegrationImportPreviewProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<PreviewFailure | null>(null);
  // The SENSO requests of the latest finished run.
  const [requests, setRequests] = useState<SensoRequest[]>([]);
  // Whether the user may look at SENSO's raw responses; false until the backend says so.
  const [canShowRawData, setCanShowRawData] = useState(false);
  const [rawDataTarget, setRawDataTarget] = useState<RawDataTarget | null>(null);
  // Invalidates responses and poll timers of a previous organization or unmounted view.
  const generation = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const loadPreview = useCallback(
    async (run: number) => {
      try {
        const loaded: PreviewData | null = await dataProvider.getIntegrationImportPreview(organizationId);
        if (run === generation.current) {
          setPreview(loaded && !isExpired(loaded) ? loaded : null);
        }
      } catch {
        if (run === generation.current) {
          notify("integration.preview.error.loading", { type: "error" });
        }
      }
    },
    [dataProvider, notify, organizationId]
  );

  const loadCanShowRawData = useCallback(
    async (run: number) => {
      let allowed = false;
      try {
        allowed = (await dataProvider.canFetchSensoRawData(organizationId)) === true;
      } catch {
        allowed = false;
      }
      if (run === generation.current) {
        setCanShowRawData(allowed);
      }
    },
    [dataProvider, organizationId]
  );

  useEffect(() => {
    generation.current += 1;
    const run = generation.current;
    setPreview(null);
    setRunning(false);
    setFailure(null);
    setRequests([]);
    setCanShowRawData(false);
    setRawDataTarget(null);
    setLoading(true);
    loadCanShowRawData(run);
    loadPreview(run).finally(() => {
      if (run === generation.current) {
        setLoading(false);
      }
    });
    return () => {
      generation.current += 1;
      clearTimeout(pollTimer.current);
    };
  }, [loadPreview, loadCanShowRawData]);

  // The backend stops returning the preview when it expires, so stop offering it as well.
  useEffect(() => {
    if (!preview) return undefined;
    const timer = setTimeout(() => setPreview(null), Math.max(new Date(preview.expiresAt).getTime() - Date.now(), 0));
    return () => clearTimeout(timer);
  }, [preview]);

  const finishRun = useCallback(
    async (run: number, { status, errorMessage, summary }: FinishedRun) => {
      if (run === generation.current) {
        setRequests(parseRequests(summary));
      }
      if (status === IntegrationExecutionStatus.Completed) {
        await loadPreview(run);
      } else if (run === generation.current) {
        setFailure({ status, reason: errorMessage || null });
      }
      if (run === generation.current) {
        setRunning(false);
      }
    },
    [loadPreview]
  );

  const pollExecution = useCallback(
    (run: number, executionId: string) => {
      pollTimer.current = setTimeout(async () => {
        try {
          const execution: FinishedRun = await dataProvider.getIntegrationExecution(organizationId, executionId);
          if (run !== generation.current) return;
          if (
            execution.status === IntegrationExecutionStatus.Pending ||
            execution.status === IntegrationExecutionStatus.Running
          ) {
            pollExecution(run, executionId);
          } else {
            await finishRun(run, execution);
          }
        } catch {
          if (run === generation.current) {
            setRunning(false);
            notify("integration.preview.error.request", { type: "error" });
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [dataProvider, finishRun, notify, organizationId]
  );

  const handleCreate = async () => {
    const run = generation.current;
    setFailure(null);
    setRequests([]);
    setRunning(true);
    try {
      const execution: FinishedRun & { id: string } = await dataProvider.previewIntegrationImport(organizationId);
      if (run !== generation.current) return;
      if (
        execution.status === IntegrationExecutionStatus.Pending ||
        execution.status === IntegrationExecutionStatus.Running
      ) {
        pollExecution(run, execution.id);
      } else {
        await finishRun(run, execution);
      }
    } catch {
      if (run === generation.current) {
        setRunning(false);
        notify("integration.preview.error.request", { type: "error" });
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 600, margin: "auto", mt: space.sm }}>
      <Typography variant="h6">{translate("integration.preview.title")}</Typography>
      <Typography variant="body2" color="text.secondary" mb={space.sm}>
        {translate("integration.preview.description")}
      </Typography>
      <Button
        color="secondary"
        variant="outlined"
        onClick={handleCreate}
        disabled={disabled || running || loading}
        label={running ? "integration.preview.button.creating" : "integration.preview.button.create"}
      />
      {disabled && (
        <Typography variant="caption" color="text.secondary" display="block" mt={space.xs}>
          {translate("integration.preview.saveFirst")}
        </Typography>
      )}
      {running && (
        <Box sx={{ mt: space.sm }} role="status">
          <Typography variant="body2">{translate("integration.preview.running")}</Typography>
          <LinearProgress sx={{ mt: space.xs }} />
        </Box>
      )}
      {failure && (
        <Alert severity="error" sx={{ mt: space.sm }}>
          {translate(
            failure.status === IntegrationExecutionStatus.Cancelled
              ? "integration.preview.error.cancelled"
              : "integration.preview.error.failed"
          )}
          {failure.reason && <Box>{translate("integration.preview.error.reason", { reason: failure.reason })}</Box>}
        </Alert>
      )}
      {failure && (
        <SensoRequestList requests={requests} canShowRawData={canShowRawData} onShowRawData={setRawDataTarget} />
      )}
      <Box sx={{ mt: space.sm }}>
        {loading && <Typography variant="body2">{translate("integration.preview.loading")}</Typography>}
        {!loading && !preview && !running && (
          <Typography variant="body2" color="text.secondary">
            {translate("integration.preview.empty")}
          </Typography>
        )}
        {!loading && preview && (
          <>
            <PreviewContent preview={preview} />
            {/* After a failed run the requests are shown under the error instead. */}
            {!failure && (
              <SensoRequestList requests={requests} canShowRawData={canShowRawData} onShowRawData={setRawDataTarget} />
            )}
            <Alert severity="info" sx={{ mt: space.sm }}>
              {translate("integration.preview.importNotice")}
            </Alert>
            <Box sx={{ mt: space.xs }}>
              <Button
                color="primary"
                variant="contained"
                onClick={onImport}
                disabled={disabled || importing || running}
                label="integration.preview.button.import"
              />
            </Box>
          </>
        )}
      </Box>
      {rawDataTarget && (
        <SensoRawDataDialog
          organizationId={organizationId}
          {...rawDataTarget}
          preview={preview}
          onClose={() => setRawDataTarget(null)}
        />
      )}
    </Box>
  );
};
